import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import queryString from "query-string";
import { useQueryClient } from "@tanstack/react-query";

import {
  QUERY_WIDTHS,
  TABLE_TRANSACTIONS_COLUMNS,
  PER_PAGE_VALUES,
} from "@utils/index";
import {
  TransactionType,
  TransactionModel,
  CategoriesModel,
  CategorySelectorModel,
  TransactionsFilterModel,
  AssetModel,
} from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import {
  useTransactionGetCategories,
  useTransactionGetAll,
  useTransactionGetOne,
  useTransactionDelete,
  useAssetGetBusinessAssets,
  useSubmissionGetAll,
} from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  PageContentContainer,
  PageContentTitle,
  Table,
  TableHead,
  TableBody,
  TrNoItems,
  TableRowTransaction,
  Pagination,
  PerPage,
  RangePicker,
  CategorySelector,
  ConfirmModal,
  BusinessSelector,
  TransactionsFilter,
  AssetSelector,
  DesktopPageTitle,
  UploadLaterBannerTransactions,
  TutorialButton,
} from "@components/index";
import { TransactionDetails } from "@pages/index";

export const TransactionsTable = () => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const location = useLocation();
  const idValue = location.pathname.includes("details")
    ? location.pathname.split("/").slice(-1)[0]
    : "";
  const navigate = useNavigate();
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const queryClient = useQueryClient();

  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );
  const businesses = useUserStore(({ businesses }) => businesses);
  const defaultBusiness = useUserStore(({ business }) => business);
  const [businessId, setBusinessId] = useState("");
  const [submissionId, setSubmissionId] = useState("");

  const [categories, setCategories] = useState<CategoriesModel>();
  const [category, setCategory] = useState<CategorySelectorModel>();

  const [assets, setAssets] = useState<AssetModel[]>([]);
  const [assetId, setAssetId] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [filter, setFilter] = useState<TransactionsFilterModel>();

  const [transactions, setTransactions] = useState<TransactionModel[]>();

  const [selectedPage, setSelectedPage] = useState(1);
  const [countTransactions, setCountTransactions] = useState(0);
  const [perPage, setPerPage] = useState(PER_PAGE_VALUES[0]);

  const [redirectNeeds, setRedirectNeeds] = useState(false);

  const [transactionId, setTransactionId] = useState("");
  const [transactionDetails, setTransactionDetails] =
    useState<TransactionModel>();
  const [removeId, setRemoveId] = useState("");

  useEffect(() => {
    const params = queryString.parse(location.search);
    setBusinessId(
      (params?.businessId as string) ||
        defaultBusiness?.uniqueId ||
        businesses[0]?.uniqueId ||
        "",
    );
    setCategory({
      type: (params?.type as TransactionType) || "",
      categoryId: (params?.categoryId as string) || "",
    });
    setAssetId((params?.assetId as string) || "");
    setSelectedPage(parseInt((params?.page || "1").toString()));
    setPerPage((params?.perPage as string) || PER_PAGE_VALUES[0]);
    setStartDate((params?.startDate as string) || "");
    setEndDate((params?.endDate as string) || "");
    setFilter({
      status: (params?.status as string) || "",
      attachment: (params?.attachment as string) || "",
      refund: (params?.refund as string) || "",
    });
    if (idValue) {
      setTransactionId(idValue);
    }
  }, [location, defaultBusiness, businesses, idValue]);

  const transactionsTitle = useMemo(() => {
    const businessTitle = businesses.find(
      ({ uniqueId }) => uniqueId === businessId,
    )?.name;
    const title = businessTitle || defaultBusiness?.name || businesses[0]?.name;
    return `${title} Transactions`;
  }, [businessId, businesses, defaultBusiness]);

  const availableTutorialNames = useMemo(() => {
    const arr = ["transactionCreateTutorial"];
    if (
      transactions?.filter(({ status }) =>
        ["open", "reopened"].includes(status.value),
      )?.length
    ) {
      arr.push("transactionEditTutorial");
      arr.push("transactionDeleteTutorial");
    }
    arr.push("refundCreateTutorial");
    if (
      transactions?.filter(({ status }) =>
        ["open", "reopened"].includes(status.value),
      )?.length
    ) {
      arr.push("transactionFindTutorial");
    }
    return arr;
  }, [transactions]);

  const {
    isPending: isPendingGetCategories,
    isSuccess: isSuccessGetCategories,
    data: dataGetCategories,
    isError: isErrorGetCategories,
    error: errorGetCategories,
  } = useTransactionGetCategories();

  useEffect(() => {
    if (isSuccessGetCategories && dataGetCategories) {
      setCategories(dataGetCategories);
    }
  }, [isSuccessGetCategories, dataGetCategories]);

  useEffect(() => {
    if (isErrorGetCategories && errorGetCategories) {
      notify("error", errorGetCategories.message);
    }
  }, [isErrorGetCategories, errorGetCategories, notify]);

  const { data: dataGetSubmissions } = useSubmissionGetAll({ businessId });

  useEffect(() => {
    if (!dataGetSubmissions?.submissions.length) return;

    if (!startDate && !endDate) {
      const first = dataGetSubmissions.submissions[0];
      setStartDate(first.period.startDate.split("T")[0]);
      setEndDate(first.period.endDate.split("T")[0]);
      setSubmissionId(first.uniqueId);
      setRedirectNeeds(true);
      return;
    }

    const match = dataGetSubmissions.submissions.find(
      ({ period }) =>
        period.startDate.split("T")[0] === startDate &&
        period.endDate.split("T")[0] === endDate,
    );
    setSubmissionId(match?.uniqueId ?? "");
  }, [startDate, endDate, dataGetSubmissions]);

  const {
    isPending: isPendingGetAssets,
    isSuccess: isSuccessGetAssets,
    data: dataGetAssets,
    isError: isErrorGetAssets,
    error: errorGetAssets,
  } = useAssetGetBusinessAssets(businessId);

  useEffect(() => {
    if (isSuccessGetAssets && dataGetAssets) {
      setAssets(dataGetAssets);
    }
  }, [isSuccessGetAssets, dataGetAssets]);

  useEffect(() => {
    if (isErrorGetAssets && errorGetAssets) {
      notify("error", errorGetAssets.message);
    }
  }, [isErrorGetAssets, errorGetAssets, notify]);

  const {
    isPending: isPendingGetTransactions,
    isSuccess: isSuccessGetTransactions,
    data: dataGetTransactions,
    isError: isErrorGetTransactions,
    error: errorGetTransactions,
  } = useTransactionGetAll({
    businessId,
    submissionId,
    type: category?.type,
    categoryId: category?.categoryId,
    assetId,
    status: filter?.status,
    attachment: filter?.attachment,
    refund: filter?.refund,
    startDate,
    endDate,
    page: selectedPage,
    perPage,
  });

  useEffect(() => {
    if (isSuccessGetTransactions && dataGetTransactions) {
      setTransactions(dataGetTransactions.transactions);
      setCountTransactions(dataGetTransactions.pagination.total);
    }
  }, [isSuccessGetTransactions, dataGetTransactions]);

  useEffect(() => {
    if (isErrorGetTransactions && errorGetTransactions) {
      notify("error", errorGetTransactions.message);
    }
  }, [isErrorGetTransactions, errorGetTransactions, notify]);

  const {
    isPending: isPendingGetOne,
    isSuccess: isSuccessGetOne,
    isStale: isStaleGetOne,
    data: dataGetOne,
    isError: isErrorGetOne,
    error: errorGetOne,
  } = useTransactionGetOne(transactionId);

  useEffect(() => {
    if (isErrorGetOne && errorGetOne) {
      notify("error", errorGetOne.message);
    }
  }, [isErrorGetOne, errorGetOne, notify]);

  useEffect(() => {
    if (isSuccessGetOne && dataGetOne) {
      setTransactionDetails(dataGetOne);
    }
  }, [isSuccessGetOne, dataGetOne]);

  const {
    mutate: mutateDeleteTransaction,
    isPending: isPendingDeleteTransaction,
  } = useTransactionDelete(
    () => {
      notify("success", "Your Transaction has been successfully deleted.");
      handleCloseDetails();
      setRemoveId("");
      queryClient.invalidateQueries({ queryKey: ["transactionGetAll"] });
    },
    ({ message }) => notify("error", message),
  );

  useEffect(() => {
    setState({
      isLoading:
        isPendingGetAssets ||
        isPendingGetCategories ||
        isPendingGetTransactions ||
        (isStaleGetOne && isPendingGetOne) ||
        isPendingDeleteTransaction,
    });
  }, [
    isPendingGetAssets,
    isPendingGetCategories,
    isPendingGetTransactions,
    isStaleGetOne,
    isPendingGetOne,
    isPendingDeleteTransaction,
    setState,
  ]);

  useEffect(() => {
    if (!redirectNeeds) {
      return;
    }
    setRedirectNeeds(false);
    const params = queryString.stringify(
      {
        businessId,
        type: category?.type,
        categoryId: category?.categoryId,
        assetId,
        status: filter?.status,
        attachment: filter?.attachment,
        refund: filter?.refund,
        page: selectedPage,
        perPage,
        startDate,
        endDate,
      },
      { skipEmptyString: true },
    );
    navigate(`?${params}`);
  }, [
    redirectNeeds,
    navigate,
    businessId,
    category,
    assetId,
    filter,
    selectedPage,
    perPage,
    startDate,
    endDate,
  ]);

  const handleSelectBusiness = (businessId: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setBusinessId(businessId);
    setSubmissionId("");
    setStartDate("");
    setEndDate("");
    setAssetId("");
  };

  const handleSelectCategory = (value: CategorySelectorModel | undefined) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setCategory(value);
  };

  const handleSelectAsset = (assetId: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setAssetId(assetId);
  };

  const handleSetStartDate = (startDate: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setStartDate(startDate);
  };

  const handleSetEndDate = (endDate: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setEndDate(endDate);
  };

  const handleSetFilter = (filter: TransactionsFilterModel) => {
    setRedirectNeeds(true);
    setFilter(filter);
  };

  const handleSelectPage = (page: number) => {
    setRedirectNeeds(true);
    setSelectedPage(page);
  };

  const handleSelectPerPage = (value: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setPerPage(value);
  };

  const handleCloseDetails = () => {
    setTransactionId("");
    setTransactionDetails(undefined);
    if (idValue) {
      navigate("/transactions");
    }
  };

  return (
    <div className="md:pt-6">
      {!isMobile && (
        <DesktopPageTitle
          title="Transactions"
          tutorialNames={availableTutorialNames}
          faqCategory={["Transactions"]}
        />
      )}
      <div className="flex flex-col justify-between gap-y-4 px-4 pt-4 md:flex-row md:px-10 md:pt-6">
        <BusinessSelector
          height="small"
          width="w-full md:w-64"
          value={businessId}
          onSetBusiness={handleSelectBusiness}
          dataTestid="filter_businessSelector"
        />
        <RangePicker
          startDate={startDate}
          endDate={endDate}
          prefix="transactionsTable"
          onSetStartDate={handleSetStartDate}
          onSetEndDate={handleSetEndDate}
          showPeriods={true}
          disableClear
          dataTestid="filter_dateRangeSelector"
        />
      </div>
      <PageContentContainer>
        <div className="flex flex-col justify-between pb-2 md:flex-row md:pb-0">
          <PageContentTitle title={transactionsTitle} />
          {isMobile && (
            <TutorialButton
              variant="dark"
              tutorialNames={availableTutorialNames}
              faqCategory={["Transactions"]}
            />
          )}
          <UploadLaterBannerTransactions
            assets={assets}
            className="md:hidden"
          />
          <div className="mt-2 flex w-full flex-col gap-4 md:mt-0 md:w-auto md:flex-row">
            {!disabledFeatures.includes("Assets") && (
              <div className="w-full md:w-[200px] lg:w-[300px]">
                <AssetSelector
                  items={assets}
                  height="small"
                  hideAddAssetItem
                  hideUploadLaterInfo
                  value={assetId}
                  onSetAsset={handleSelectAsset}
                  dataTestid="filter_assetSelector"
                />
              </div>
            )}
            <div className="w-full md:w-[200px] lg:w-[300px] xl:w-[460px]">
              <CategorySelector
                label={
                  <>
                    <span className="text-black-100">Filter By:</span> Type,
                    Category, Subcategory
                  </>
                }
                height="small"
                categories={categories}
                value={category}
                variant="filter"
                onSetCategory={handleSelectCategory}
                dataTestid="filter_categorySelector"
              />
            </div>
            <TransactionsFilter
              value={filter}
              onSetFilters={handleSetFilter}
              dataTestid="filter_filtersSelector"
            />
          </div>
        </div>
        <UploadLaterBannerTransactions
          assets={assets}
          className="hidden md:flex"
        />
        <Table className="pb-4 md:pb-0" dataTestid="table_transactionsTable">
          {!isMobile && (
            <TableHead
              columns={TABLE_TRANSACTIONS_COLUMNS.filter(
                ({ title }) =>
                  title !== "Asset" || !disabledFeatures.includes("Assets"),
              )}
              dataTestid="thead_transactionsTable"
            />
          )}
          <TableBody dataTestid="tbody_transactionsTable">
            {transactions?.length ? (
              transactions.map((transaction) => (
                <TableRowTransaction
                  key={transaction.uniqueId}
                  transaction={transaction}
                  hideAssetColumn={disabledFeatures.includes("Assets")}
                  onDetails={() => setTransactionId(transaction.uniqueId)}
                  onRemove={() => setRemoveId(transaction.uniqueId)}
                />
              ))
            ) : (
              <TrNoItems title="No transactions found" />
            )}
          </TableBody>
        </Table>
        {countTransactions > 0 && (
          <div className="flex flex-col gap-y-6 md:flex-row md:gap-x-6">
            <Pagination
              count={countTransactions}
              perPage={parseInt(perPage, 10)}
              currentPage={selectedPage}
              onSelect={handleSelectPage}
            />
            <PerPage perPage={perPage} onSelect={handleSelectPerPage} />
          </div>
        )}
      </PageContentContainer>
      <TransactionDetails
        transaction={transactionDetails}
        onRemove={() => mutateDeleteTransaction(transactionDetails!.uniqueId)}
        onClose={handleCloseDetails}
      />
      <ConfirmModal
        show={!!removeId}
        confirmButtonTitle="Delete"
        text1="Are you sure?"
        text2="Do you really want to delete this transaction?"
        onClickCancel={() => setRemoveId("")}
        onClickConfirm={() => mutateDeleteTransaction(removeId)}
      />
    </div>
  );
};
